// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Servicio de API - Maneja todas las llamadas al backend.
 * Todas las peticiones HTTP pasan por apiClient (Authorization + credentials).
 */

import { API_ENDPOINTS, STORAGE_KEYS, BASE_PATH, isFullSpaAuthPath } from '../utils/constants';
import { authHeaders, getToken, isPublicTokenGuardUrl } from './apiClient';
import { apiAuthMethods } from '../api/apiAuth';
import { apiContactsMethods } from '../api/apiContacts';
import { apiMessagesMethods } from '../api/apiMessages';
import { apiProfileMethods } from '../api/apiProfile';
import { apiAdminMethods } from '../api/apiAdmin';
import { apiReviewsMethods } from '../api/apiReviews';
import { errorBus } from '../core/errors/errorBus';
import { BUILD_ID } from '../config/buildInfo';
import { isDevErrorUnifiedModal } from '../config/devErrorOverlay';
import { breadcrumbApi } from './errorBreadcrumbs';
import { resolveApiErrorMessage } from '../utils/errorMessages';
import { devLog } from '../utils/devConsoleLog';

/**
 * DEV ERROR UNIFIED OVERLAY
 * Solo activo en entorno desarrollo.
 * Permite visualizar error técnico sin doble modal.
 * NO habilitar en producción.
 */
let apiErrorHandler = null;
export function setApiErrorHandler(fn) {
  apiErrorHandler = typeof fn === 'function' ? fn : null;
}

if (typeof window !== 'undefined') {
  console.warn('[RUNTIME FILE]', 'api.js v=' + import.meta.url);
  devLog('[API SAFE MODE ACTIVE]');
  devLog('[SESSION FIX ACTIVE]');
  devLog('[SESSION CHECK]', document.cookie);
}

const API_BASE = '/api';
const LOGIN_ENDPOINT_ABSOLUTE = '/api/auth/login.php';

/**
 * Login/password o Google login: mismo tratamiento UX para errores de límite de dispositivos.
 * @param {string} endpointStr
 * @param {string} [url]
 * @returns {boolean}
 */
function isAuthLoginFlowEndpoint(endpointStr, url) {
  const ep = typeof endpointStr === 'string' ? endpointStr : '';
  const u = typeof url === 'string' ? url : '';
  const s = `${ep} ${u}`.toLowerCase();
  return s.includes('auth/login') || s.includes('google_login');
}

/** @param {string} [code] */
function isDeviceLimitReachedCode(code) {
  return code === 'auth.device_limit_reached' || code === 'SAAS_MAX_DEVICES';
}

/** Credenciales incorrectas en login: feedback inline en LoginForm; no modal global (evita parpadeo y doble UX). */
function isLoginInvalidCredentialsCode(code) {
  if (code == null || typeof code !== 'string') return false;
  return (
    code === 'auth.invalid_credentials'
    || code === 'AUTH_INVALID_CREDENTIALS'
    || code === 'errors.auth_invalid_credentials'
  );
}

/** Cuenta sin activar (403): el login maneja UX dedicada; no modal técnico global con HTTP/build. */
function isLoginUserInactiveCode(code) {
  if (code == null || typeof code !== 'string') return false;
  return code === 'auth.user_inactive';
}

/** CSRF desalineado (p. ej. token viejo en localStorage con mismo BUILD_ID tras deploy): retry silencioso en api.js. */
function isLoginCsrfInvalidCode(code) {
  if (code == null || typeof code !== 'string') return false;
  return code === 'auth.csrf_invalid';
}

/** @param {Record<string, unknown>|null|undefined} data */
function isLoginUserInactivePayload(data) {
  if (!data || typeof data !== 'object') return false;
  if (data.code === 'auth.user_inactive') return true;
  return data.error === 'USER_INACTIVE';
}

function buildApiUrl(endpoint) {
  if (typeof endpoint !== 'string') return API_BASE;
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) return endpoint;
  const normalized = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  if (normalized.startsWith('/api/')) return normalized;
  return `${API_BASE}${normalized}`;
}

function isUserNotFoundValue(value) {
  if (value == null) return false;
  const normalized = String(value).trim().toLowerCase();
  return normalized === 'user_not_found'
    || normalized === 'auth.user_not_found'
    || normalized.includes('user not found')
    || normalized.includes('user_not_found');
}

function isNonBlockingUserLookupEndpoint(endpoint) {
  if (typeof endpoint !== 'string' || !endpoint.trim()) return false;
  const normalized = endpoint.toLowerCase();
  return normalized.includes('diag')
    || normalized.includes('/user/status')
    || normalized.includes('/api/user/')
    || normalized.includes('/perfil.php')
    || normalized.includes('/user/profile.php')
    || normalized.includes('/profile')
    || normalized.includes('/auth/check.php')
    || normalized.includes('/api/auth/check.php')
    || normalized.endsWith('/check.php');
}

function shouldIgnoreSecondaryUserNotFound(endpoint, payload) {
  if (!isNonBlockingUserLookupEndpoint(endpoint)) return false;
  if (!payload || typeof payload !== 'object') return false;
  return isUserNotFoundValue(payload.error)
    || isUserNotFoundValue(payload.code)
    || isUserNotFoundValue(payload.message)
    || isUserNotFoundValue(payload.message_key);
}

async function safeFetch(url, options) {
  const res = await fetch(url, {
    credentials: 'include',
    ...options,
  });

  if (res.status === 401) {
    let data401 = {};
    try {
      const text = await res.text();
      if (text && text.trim()) data401 = JSON.parse(text);
    } catch (_) {}
    console.warn('[API 401] Sesión inválida o expirada', data401?.code || '');
    return {
      success: false,
      unauthorized: true,
      status: 401,
      code: data401?.code || 'TOKEN_INVALID',
      ...data401,
    };
  }

  let data;
  const textFallbackResponse = res.clone();
  try {
    data = await res.json();
  } catch (e) {
    const rawText = await textFallbackResponse.text().catch(() => '');
    console.error('[INVALID JSON RESPONSE]', rawText);
    throw new Error('Invalid JSON response');
  }

  if (!res.ok) {
    console.error('[API ERROR]', data);
    const err = new Error(
      data?.error || data?.code || data?.message || 'API error'
    );
    err.code = data?.code;
    err.data = data;
    err.status = res.status;
    throw err;
  }

  return { res, data };
}

// Cache/dedupe liviano (evita requests duplicados en montajes rápidos)
const inflightGet = new Map(); // key -> Promise
const responseCache = new Map(); // key -> { exp, data }
const DEFAULT_GET_TTL_MS = 15000; // 15s

/** Contexto opcional para el próximo error emitido al bus y para "Reportar a soporte" (ej. reporter_identity desde LoginView). No enviar passwords. */
let reportContext = {};
export function setReportContext(ctx) {
  reportContext = ctx && typeof ctx === 'object' ? { ...ctx } : {};
}
export function clearReportContext() {
  reportContext = {};
}
/** Devuelve una copia del contexto actual (para resolver reporter_identity al enviar el reporte). */
export function getReportContext() {
  return reportContext && typeof reportContext === 'object' ? { ...reportContext } : {};
}

/**
 * Limpia cachés de API (inflight y response). Usar en logout para evitar rehidratación.
 */
export function clearApiCaches() {
  inflightGet.clear();
  responseCache.clear();
}

/**
 * Invalida entradas de cache cuya key contenga alguno de los patrones dados.
 * @param {string[]} patterns - Substrings a buscar en las keys del cache.
 */
export function invalidateCacheByPattern(patterns) {
  if (!Array.isArray(patterns) || patterns.length === 0) return;
  for (const key of responseCache.keys()) {
    if (patterns.some((p) => key.includes(p))) {
      responseCache.delete(key);
    }
  }
}

/**
 * Servicio principal de API
 */
export const apiService = {
  /**
   * Realiza una petición HTTP
   * @param {string} endpoint - Endpoint de la API
   * @param {Object} options - Opciones de fetch
   * @returns {Promise<Object>} Respuesta de la API
   */
  async request(endpoint, options = {}) {
    const skipGlobalErrorModal = options?._skipGlobalErrorModal === true;
    // Correlation ID: único por request para tracing backend/frontend
    const requestId = options.headers?.['X-Request-ID'] ||
      (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`);
    const attachRequestId = (e) => {
      if (e && typeof e === 'object') e.requestId = requestId;
      return e;
    };
    if (typeof window !== 'undefined') {
      window.__archatLastRequestId = requestId;
    }

    const endpointStr = typeof endpoint === 'string' ? endpoint : '';
    const isLoginRequest = endpoint === API_ENDPOINTS.LOGIN
      || endpoint === LOGIN_ENDPOINT_ABSOLUTE
      || endpointStr.includes('auth/login');

    let url = isLoginRequest ? LOGIN_ENDPOINT_ABSOLUTE : buildApiUrl(endpoint);
    const isAuthLoginFlow = isLoginRequest || isAuthLoginFlowEndpoint(endpointStr, url);
    if (isLoginRequest) {
      devLog('[LOGIN URL]', LOGIN_ENDPOINT_ABSOLUTE);
    }

    const badApiPrefix = `${BASE_PATH.replace(/\/$/, '')}/api`;
    if (typeof url === 'string' && url.includes(badApiPrefix)) {
      console.error('[CRITICAL BUG] URL INVALIDA:', url);
    }
    devLog('[API FINAL URL]', url);
    const token = getToken();
    console.warn('[TOKEN_CURRENT]', token);
    const isPublic = isPublicTokenGuardUrl(endpointStr) || isPublicTokenGuardUrl(url);
    const source = options?.__source || 'apiService.request';
    const pathname = (typeof window !== 'undefined' && window.location) ? window.location.pathname : '';
    const pathForGlobalError = endpointStr || (typeof url === 'string' ? url : '') || pathname || '';
    const isPublicAuthRoute = isFullSpaAuthPath(pathname);
    const traceStack = (new Error().stack || '').split('\n').slice(1, 4).join(' | ');
    devLog('[AUTH_TRACE]', {
      source,
      endpoint: endpointStr || url,
      url,
      hasToken: !!token,
      isPublic,
      pathname,
      wrapper: 'api.js',
      stack: traceStack,
    });
    /* Login por contraseña: nunca bloquear por guard de token (isPublic puede fallar si cambia la URL). */
    if (!token && !isPublic && !isLoginRequest) {
      console.warn('[BLOCKED_NO_TOKEN]', { endpoint: endpointStr || url, source, wrapper: 'api.js' });
      console.warn('[AUTH_TRACE_BLOCKED]', {
        source,
        endpoint: endpointStr || url,
        url,
        hasToken: false,
        isPublic: false,
        pathname,
        wrapper: 'api.js',
      });
      if (isPublicAuthRoute) {
        return Promise.resolve({
          success: false,
          blocked: true,
          silent_preauth: true,
          code: 'PREAUTH_BLOCKED',
          message: 'auth.preauth_blocked',
        });
      }
      return Promise.resolve({
        success: false,
        blocked: true,
        code: 'TOKEN_MISSING',
        message: 'auth.token_missing'
      });
    }

    // Billing/checkout: no bloquear en cliente por FEATURES.PAYMENTS_ENABLED (flag estático desactualizado en prod).
    // Quién puede operar: UI con canOperateBilling(planStatus); backend con allowlist / PAYMENTS_*.

    // Login: log SIEMPRE para diagnóstico UI 401 vs backend 200 (sin valores sensibles)
    if (isLoginRequest) {
      const bodyStr = options.body && typeof options.body === 'string' ? options.body : '';
      let payloadKeys = [];
      try {
        if (bodyStr) payloadKeys = Object.keys(JSON.parse(bodyStr));
      } catch (_) { payloadKeys = ['(parse failed)']; }
      devLog('[LOGIN][REQUEST]', {
        finalUrl: url,
        reqHeaderSent: requestId,
        payloadKeys,
        contentLength: bodyStr ? bodyStr.length : 0,
      });
    }
    breadcrumbApi(endpoint);

    const method = (options.method || 'GET').toUpperCase();

    // No cachear GET que requieren auth ni historial de mensajes — siempre datos frescos
    const authRequiredPatterns = [
      'perfil',
      'profile',
      'plan/info',
      'plan/',
      'tenant/usage',
      'metrics',
      'mensajes',
      'dashboard_recent_inbound',
      'export_conversation',
      'presence',
      'favoritos',
      'contact_pin_archive',
      'contactos',
      'files.php', // Mis archivos: nunca servir listado en caché (delete_for_me debe verse tras refresh)
    ];
    const isAuthRequiredGet = method === 'GET' && authRequiredPatterns.some(p => endpoint.includes(p));
    const skipCache = options._skipDedupe || isAuthRequiredGet;

    // Cache solo para GET no sensibles; dedupe de inflight
    if (method === 'GET' && !skipCache) {
      const cacheKey = url;
      const cached = responseCache.get(cacheKey);
      if (cached && cached.exp > Date.now()) {
        return cached.data;
      }
      if (inflightGet.has(cacheKey)) {
        return inflightGet.get(cacheKey);
      }
      const p = (async () => {
        try {
          const res = await this.request(endpoint, { ...options, method: 'GET', _skipDedupe: true });
          responseCache.set(cacheKey, { exp: Date.now() + DEFAULT_GET_TTL_MS, data: res });
          return res;
        } finally {
          inflightGet.delete(cacheKey);
        }
      })();
      inflightGet.set(cacheKey, p);
      return p;
    }
    
    const csrfToken = localStorage.getItem('archat_csrf_token');
    const tenantId = localStorage.getItem('archat_tenant_id');
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    /**
     * Login por contraseña: NUNCA enviar Bearer de sesión previa.
     * Si no, el backend puede tratar la petición como ya autenticada y devolver éxito sin validar la contraseña (bypass crítico en el 2.º intento).
     */
    const authH = isLoginRequest ? {} : authHeaders();
    const isProfileRequest = endpoint.includes('perfil') || endpoint.includes('profile');
    const headers = {
      ...(!isFormData && { 'Content-Type': 'application/json' }),
      'X-Request-ID': requestId,
      ...(csrfToken && { 'X-CSRF-Token': csrfToken }),
      ...(tenantId && { 'X-Tenant-ID': tenantId }),
      ...(options.headers && typeof options.headers === 'object' ? options.headers : {}),
      ...authH
    };
    if (isFormData) {
      delete headers['Content-Type'];
    }
    if (typeof endpoint === 'string' && endpoint.includes('suggestions')) {
      if (typeof console !== 'undefined') {
        console.warn('[SUGGESTIONS] auth token present', !!headers.Authorization);
      }
    }
    const defaultOptions = {
      ...options,
      withCredentials: true,
      credentials: 'include',
      headers
    };
    console.warn('[AUTH_HEADER]', defaultOptions?.headers?.Authorization || null);
    devLog('[API_REQUEST]', {
      url,
      hasToken: !!token,
      isPublic,
      source,
      wrapper: 'api.js',
      pathname,
    });

    if (isProfileRequest) {
      const tokenPresent = !!getToken();
      if (typeof console !== 'undefined' && console.debug) {
        console.debug('[API][PROFILE] auth token present:', tokenPresent);
      }
    }

    const debug = localStorage.getItem('archat_debug_api') === '1';
    if (debug && (options.method === 'PUT' || options.method === 'POST')) {
      devLog(`🔄 [API] ${options.method} Request:`, {
        url,
        endpoint,
        method: options.method,
        body: options.body,
        headers: defaultOptions.headers
      });
    }

    try {
      if (debug) devLog(`📡 [API] Enviando ${options.method || 'GET'} a:`, url);
      if (import.meta.env.DEV) {
        console.debug('[AUTH TRACE FRONT]', { url, tokenPresent: !!getToken(), headers: defaultOptions.headers });
      }
      let response;
      let data;
      try {
        const result = await safeFetch(url, defaultOptions);
        if (result?.unauthorized === true) {
          // Login 401: credenciales incorrectas (no es sesión expirada / TOKEN_INVALID).
          if (isLoginRequest && (result.error === 'INVALID_CREDENTIALS' || result.error === 'invalid_credentials')) {
            return {
              success: false,
              status: 401,
              code: 'auth.invalid_credentials',
              error: result.error || 'INVALID_CREDENTIALS',
            };
          }
          if (getToken() && options._skipForceLogout !== true) {
            try {
              localStorage.removeItem(STORAGE_KEYS.TOKEN);
              localStorage.removeItem(STORAGE_KEYS.USER_ID);
              localStorage.removeItem(STORAGE_KEYS.USER_DATA);
              localStorage.removeItem('archat_tenant_id');
              localStorage.removeItem('archat_csrf_token');
            } catch (_) {}
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('archat:force-logout'));
            }
          }
          return result;
        }
        response = result.res;
        data = result.data;
      } catch (fetchError) {
        // 403 esperado en historial: usuario/conversación no alineados (p. ej. tras reimportar DB o sesión obsoleta).
        // No lanzar: devolver JSON para que useMessages maneje sin modal global técnico.
        if (
          fetchError &&
          Number(fetchError.status) === 403 &&
          fetchError.data &&
          typeof fetchError.data === 'object'
        ) {
          const denyCode = fetchError.data.code;
          const ep = endpointStr || '';
          if (
            (denyCode === 'CONVERSATION_ACCESS_DENIED' || denyCode === 'USER_MISMATCH') &&
            typeof ep === 'string' &&
            ep.includes('mensajes')
          ) {
            attachRequestId(fetchError);
            return {
              success: false,
              error: fetchError.data.error || fetchError.message || 'forbidden',
              code: denyCode,
              mensajes: [],
              data: [],
              status: 403,
              request_id: fetchError.data.request_id ?? requestId,
            };
          }
        }
        // Login contraseña: CSRF obsoleto (sesión PHP nueva vs archat_csrf_token viejo; BUILD_ID puede no cambiar).
        // login.php devuelve 403 + { code, csrf_token } — persistir token fresco y reintentar UNA vez (sin loop).
        if (
          isLoginRequest
          && Number(fetchError?.status) === 403
          && fetchError?.data
          && typeof fetchError.data === 'object'
          && fetchError.data.code === 'auth.csrf_invalid'
          && options._csrfLoginRetried !== true
        ) {
          try {
            const fresh = fetchError.data.csrf_token;
            if (typeof fresh === 'string' && fresh.trim()) {
              localStorage.setItem('archat_csrf_token', fresh.trim());
            } else {
              localStorage.removeItem('archat_csrf_token');
            }
          } catch (_) { /* noop */ }
          return this.request(endpoint, { ...options, _csrfLoginRetried: true });
        }
        if (fetchError && (fetchError.status || fetchError.data || fetchError.code)) {
          attachRequestId(fetchError);
          throw fetchError;
        }
        // 🔥 Error de red (fetch falló completamente)
        const error = new Error();
        error.code = 'NETWORK_ERROR';
        error.networkError = fetchError.message;
        error.data = { url, endpoint };
        attachRequestId(error);
        throw error;
      }
      if (debug) {
        devLog(`📡 [API] Respuesta recibida:`, {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          headers: Object.fromEntries(response.headers.entries())
        });
      }
      if (typeof endpoint === 'string' && endpoint.includes('mensajes')) {
        devLog('[API][MSG] status', response.status, 'ok', response.ok, 'url', url);
      }

      // Lista vacía u homogénea: algunos GET devuelven JSON `[]` con 200 (válido; no es error).
      if (Array.isArray(data) && response.ok) {
        return { success: true, data };
      }

      if (!data || typeof data !== 'object' || Array.isArray(data)) {
        try {
          errorBus.emit({
            type: 'system',
            code: 'INVALID_RESPONSE',
            message: 'Respuesta inválida del servidor',
            mode: 'technical',
          });
        } catch (_) {}
        const err = new Error('INVALID_RESPONSE');
        err.code = 'INVALID_RESPONSE';
        err.data = data;
        attachRequestId(err);
        throw err;
      }

      if (data?.csrf_token) {
        localStorage.setItem('archat_csrf_token', data.csrf_token);
      }

      if (data?.error === 'unauthorized') {
        return data;
      }

      if (shouldIgnoreSecondaryUserNotFound(endpointStr, data)) {
        console.warn('[API][IGNORED_USER_NOT_FOUND]', endpointStr);
        return {
          success: false,
          ignored: true,
          reason: 'user_not_found_secondary',
        };
      }

      if (endpointStr.includes('diag') && data?.success === false) {
        console.warn('[IGNORED DIAG ERROR]', endpointStr);
        return { success: false };
      }

      if (data.success === false) {
        if (data?.blocked === true) {
          return data;
        }
        if (data.code === 'ALREADY_CONTACT') {
          const currentLang = typeof localStorage !== 'undefined'
            ? localStorage.getItem(STORAGE_KEYS.LANGUAGE)
            : 'en';
          const friendly = resolveApiErrorMessage('ALREADY_CONTACT', currentLang);
          return {
            ...data,
            success: true,
            logicalSuccess: true,
            alreadyContact: true,
            uiMessage: friendly?.message,
            uiType: friendly?.type || 'info',
          };
        }
        const simpleTypes = ['validation', 'business', 'auth'];
        const type = data.type || 'system';
        const mode = simpleTypes.includes(type) ? 'simple' : 'technical';
        const suppressGlobalForLoginDeviceLimit =
          isAuthLoginFlow && isDeviceLimitReachedCode(data.code);
        const suppressGlobalForLoginInvalidCreds =
          isAuthLoginFlow && isLoginInvalidCredentialsCode(data.code);
        const suppressGlobalForLoginUserInactive =
          isAuthLoginFlow && isLoginUserInactivePayload(data);
        const suppressGlobalForLoginCsrfInvalid =
          isAuthLoginFlow && isLoginCsrfInvalidCode(data.code);
        if (!skipGlobalErrorModal && !suppressGlobalForLoginDeviceLimit && !suppressGlobalForLoginInvalidCreds && !suppressGlobalForLoginUserInactive && !suppressGlobalForLoginCsrfInvalid) {
          try {
            errorBus.emit({
              ...data,
              mode,
              code: data.code || 'api.error',
              requestId: data.request_id || requestId,
              status: Number(response?.status || data?.status || 0) || null,
              path: pathForGlobalError,
              buildId: BUILD_ID,
              timestamp: new Date().toISOString(),
              ...reportContext,
            });
          } catch (_) {}
        }
        if (data?.code === 'TOKEN_INVALID' && options._skipForceLogout !== true) {
          try {
            localStorage.removeItem(STORAGE_KEYS.TOKEN);
            localStorage.removeItem(STORAGE_KEYS.USER_ID);
            localStorage.removeItem(STORAGE_KEYS.USER_DATA);
            localStorage.removeItem('archat_tenant_id');
            localStorage.removeItem('archat_csrf_token');
          } catch (_) {}
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('archat:force-logout'));
          }
        }
        const err = new Error(data.message || 'Request failed');
        err.code = data.code;
        err.data = data;
        err.success = false;
        attachRequestId(err);
        throw err;
      }

      return data;
    } catch (error) {
      // Diagnóstico: formato tipo axios (config/response) + fallback fetch (status/data/url local)
      console.error('[API_ERROR_TRACE]', {
        url: error?.config?.url ?? url,
        status: error?.response?.status ?? error?.status ?? null,
        data: error?.response?.data ?? error?.data ?? null,
      });
      console.error('[API FULL ERROR]', error);
      attachRequestId(error);
      const responseData = error?.response?.data ?? error?.data;
      if (responseData?.blocked === true) {
        return responseData;
      }
      if (shouldIgnoreSecondaryUserNotFound(endpointStr, responseData || {})) {
        console.warn('[API][IGNORED_USER_NOT_FOUND]', endpointStr);
        return {
          success: false,
          ignored: true,
          reason: 'user_not_found_secondary',
        };
      }
      if (responseData && responseData.error_key === 'avatar.errors.too_large') {
        return Promise.reject({ error_key: responseData.error_key ?? 'avatar.errors.too_large' });
      }
      if (error.message) {
        console.error(`Error en ${endpoint}:`, error.message);
      } else if (typeof window !== 'undefined' && window.logger) {
        window.logger.error(`Error de red en ${endpoint}`, error);
      }

      // Ya emitido en el try (INVALID_RESPONSE, EMPTY_RESPONSE, NETWORK_ERROR); no duplicar modal.
      if (['INVALID_RESPONSE', 'EMPTY_RESPONSE', 'NETWORK_ERROR'].includes(error.code)) {
        throw error;
      }

      const errCodeForGlobalBus =
        (responseData && typeof responseData === 'object' ? responseData.code : null)
        ?? error?.code
        ?? (error?.data && typeof error.data === 'object' ? error.data.code : null);
      const suppressGlobalForLoginDeviceLimitCatch =
        isAuthLoginFlow && isDeviceLimitReachedCode(errCodeForGlobalBus);
      const suppressGlobalForLoginInvalidCredsCatch =
        isAuthLoginFlow && isLoginInvalidCredentialsCode(errCodeForGlobalBus);
      const suppressGlobalForLoginUserInactiveCatch =
        isAuthLoginFlow && (isLoginUserInactiveCode(errCodeForGlobalBus) || isLoginUserInactivePayload(responseData));
      const suppressGlobalForLoginCsrfInvalidCatch =
        isAuthLoginFlow && isLoginCsrfInvalidCode(errCodeForGlobalBus);

      if (!suppressGlobalForLoginDeviceLimitCatch && !suppressGlobalForLoginInvalidCredsCatch && !suppressGlobalForLoginUserInactiveCatch && !suppressGlobalForLoginCsrfInvalidCatch && !skipGlobalErrorModal && isDevErrorUnifiedModal() && apiErrorHandler) {
        try {
          apiErrorHandler({
            userMessage: error.message || 'Error de conexión',
            technicalMessage: [error.message, error.code, `HTTP:${error?.response?.status ?? error?.status ?? 'N/A'}`].filter(Boolean).join(' | '),
            endpoint: typeof endpoint === 'string' ? endpoint : '',
            requestId: error.requestId || error.request_id || null,
          });
          error.__handled = true;
        } catch (_) {}
      } else if (!suppressGlobalForLoginDeviceLimitCatch && !suppressGlobalForLoginInvalidCredsCatch && !suppressGlobalForLoginUserInactiveCatch && !suppressGlobalForLoginCsrfInvalidCatch && !skipGlobalErrorModal) {
        const type = responseData?.type ?? error?.data?.type ?? 'system';
        const simpleTypes = ['validation', 'business', 'auth'];
        const mode = simpleTypes.includes(type) ? 'simple' : 'technical';
        try {
          errorBus.emit({
            code: error.code || 'E-API-ERROR',
            message: error.message || 'Error de conexión',
            message_key: responseData?.message_key ?? error?.data?.message_key,
            current: responseData?.current ?? error?.data?.current,
            limit: responseData?.limit ?? error?.data?.limit,
            mode,
            requestId: error.requestId || error.request_id || null,
            status: Number(error?.response?.status ?? error?.status ?? responseData?.status ?? error?.data?.status ?? 0) || null,
            endpoint: endpointStr || (typeof url === 'string' ? url : ''),
            path: pathForGlobalError,
            buildId: BUILD_ID,
            timestamp: new Date().toISOString(),
            ...reportContext,
          });
        } catch (_) {}
      }
      throw error;
    }
  },

  async getMetricsSummary() {
    return this.request('/api/admin/metrics_summary.php', { method: 'GET' });
  },

  async getEmailMetrics() {
    return this.request('/api/admin/email_metrics.php', { method: 'GET' });
  },

  async getFunnelMetrics() {
    return this.request('/api/admin/funnel_metrics.php', { method: 'GET' });
  }
};
import { apiFilesMethods } from '../api/apiFiles';
import { apiTagsMethods } from '../api/apiTags';
import { apiMessageTemplatesMethods } from '../api/apiMessageTemplates';
Object.assign(apiService, apiAuthMethods, apiContactsMethods, apiMessagesMethods, apiProfileMethods, apiAdminMethods, apiReviewsMethods, apiFilesMethods, apiTagsMethods, apiMessageTemplatesMethods);
